package com.kanati.shared.ticket

import com.kanati.shared.commerce.SaleResponse
import com.kanati.shared.commerce.ClientTerminalPaymentSessionResponse
import com.kanati.shared.commerce.TerminalPaymentSessionResponse
import kotlinx.serialization.Serializable

@Serializable
enum class TicketEntitlementType {
    CONCESSION_CREDIT,
    FREE_ITEM_PER_EVENT,
    SEASON_PASS,
    MERCH_CREDIT
}

@Serializable
data class TicketEntitlementDto(
    val type: TicketEntitlementType,
    val label: String,
    val amount: Int? = null,
    val quantity: Int? = null,
)

@Serializable
enum class TicketStatus {
    VALID,
    UNUSED,
    INVALID,
    USED,
    ADMITTED,
    VOID,
    RELEASED,
}

@Serializable
enum class TicketValidationOutcome {
    GRANTED,
    DENIED,
    PROVISIONAL_GRANTED,
    RECONCILED_GRANTED,
    RECONCILED_DENIED,
    /** Conflicting reuse of a scoped localScanId; no ticket or validation-record mutation. */
    RECONCILIATION_CONFLICT,
}

@Serializable
enum class PaperTicketBatchStatus {
    ACTIVE,
    RELEASED,
    CLOSED,
}

@Serializable
data class TicketDto(
    val id: String,
    val eventId: String,
    val districtId: String,
    val schoolId: String = "",
    val ticketTierId: String,
    val ticketTierName: String,
    val ticketTierPriceAmount: Int,
    val ticketCode: String,
    val status: TicketStatus,
    val paperBatchId: String? = null,
    val entitlements: List<TicketEntitlementDto> = emptyList(),
    val holderName: String?,
    val createdAt: String,
    val usedAt: String?,
)

@Serializable
data class IssueTicketRequest(
    val eventId: String,
    val districtId: String,
    val schoolId: String = "",
    val ticketTierId: String,
    val holderName: String? = null,
)

@Serializable
data class CreatePaperTicketBatchRequest(
    val eventId: String,
    val districtId: String,
    val schoolId: String = "",
    val ticketTierId: String,
    val quantity: Int,
)

@Serializable
data class SellTicketRequest(
    val eventId: String,
    val districtId: String,
    val schoolId: String = "",
    val ticketTierId: String,
    val paymentMethod: String,
    val paymentIntentId: String? = null,
    val holderName: String? = null,
)

@Serializable
data class StartTerminalTicketSalePaymentRequest(
    val sale: SellTicketRequest,
    val terminalReaderId: String,
)

@Serializable
data class StartClientTerminalTicketSalePaymentRequest(
    val sale: SellTicketRequest,
)

@Serializable
data class FinalizeClientTerminalTicketPaymentRequest(
    val paymentSessionId: String,
)

@Serializable
data class TicketCheckInRequest(
    val ticketCode: String,
)

@Serializable
data class ValidateTicketRequest(
    val ticketCode: String,
    val scannedAt: String? = null,
)

/**
 * A backend admission decision. Transport/service failures are not DENIED/INVALID responses.
 * DENIED with ADMITTED means a paper ticket was already used. Wrong-event and unknown-ticket
 * denials both use INVALID; message is display text, not a stable denial-reason code.
 */
@Serializable
data class TicketValidationResponse(
    val success: Boolean,
    val outcome: TicketValidationOutcome,
    val ticketStatus: TicketStatus,
    val message: String,
    val ticket: TicketDto? = null,
    val validationRecordId: String? = null,
    val validatedAt: String,
)

@Serializable
data class ValidationSnapshotTicketDto(
    val ticketId: String,
    val ticketCode: String,
    val ticketTierId: String,
    val ticketTierName: String,
    val status: TicketStatus,
)

/** Shared shape for deferred bounded offline validation; availability requires backend/POS support. */
@Serializable
data class ValidationSnapshotResponse(
    val districtId: String,
    val schoolId: String,
    val eventId: String,
    val generatedAt: String,
    val expiresAt: String,
    val tickets: List<ValidationSnapshotTicketDto>,
)

/** localScanId must remain stable across retries; this DTO does not implement reconciliation. */
@Serializable
data class OfflineValidationReconciliationRequest(
    val localScanId: String,
    val districtId: String,
    val schoolId: String,
    val eventId: String,
    val ticketCode: String,
    val scannedAt: String,
    val provisionalOutcome: TicketValidationOutcome,
)

@Serializable
data class SellTicketResponse(
    val sale: SaleResponse,
    val ticket: TicketDto,
)

@Serializable
data class RecoverTerminalTicketPaymentResponse(
    val session: TerminalPaymentSessionResponse,
    val sale: SaleResponse? = null,
    val ticket: TicketDto? = null,
)

@Serializable
data class RecoverClientTerminalTicketPaymentResponse(
    val session: ClientTerminalPaymentSessionResponse,
    val sale: SaleResponse? = null,
    val ticket: TicketDto? = null,
)

@Serializable
data class CreatePaperTicketBatchResponse(
    val batch: PaperTicketBatchDto,
    val tickets: List<TicketDto>,
)

@Serializable
data class EventIssuedTicketsResponse(
    val eventId: String,
    val tickets: List<TicketDto>,
)

@Serializable
data class PaperTicketBatchDto(
    val id: String,
    val eventId: String,
    val districtId: String,
    val schoolId: String = "",
    val ticketTierId: String,
    val ticketTierName: String,
    val reservedQuantity: Int,
    val admittedQuantity: Int,
    val releasedQuantity: Int,
    val remainingQuantity: Int,
    val status: PaperTicketBatchStatus,
    val createdAt: String,
)

@Serializable
data class EventPaperTicketBatchesResponse(
    val eventId: String,
    val batches: List<PaperTicketBatchDto>,
)

@Serializable
data class ReleasePaperTicketBatchRequest(
    val releaseQuantity: Int,
)

@Serializable
data class TicketCheckInResponse(
    val success: Boolean,
    val status: TicketStatus,
    val message: String,
    val ticket: TicketDto? = null,
)
